import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { searchPrescribeMedicines } from '../api/pharmacy';
import { medicineOptionLabel } from '../utils/pharmacyMappers';
import { formatCurrency } from '../utils';
import { inputSx } from '../theme';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const PAGE_LIMIT = 50;
const LOAD_MORE_OPTION = { __loadMore: true, id: '__load_more__' };

function getMedicineId(item) {
  return item?.item_id || item?.id || '';
}

function mergeMedicines(existing, incoming) {
  const map = new Map(existing.map((item) => [getMedicineId(item), item]));
  incoming.forEach((item) => {
    const id = getMedicineId(item);
    if (id) map.set(id, item);
  });
  return Array.from(map.values());
}

/**
 * Debounced medicine search — GET /inventory/pharmacy/prescribe/search/.
 * Type-to-search only (min 2 chars). When requireStore is false (walk-in),
 * searches with tenant/hospital only — no location_id.
 */
export default function PharmacyMedicineAutocomplete({
  value,
  /**
   * The already-resolved item for `value`. Without it a caller that seeds
   * `value` from outside the dropdown (e.g. an availability match) renders a
   * blank input, because the label is otherwise only found in fetched options.
   */
  valueItem = null,
  onChange,
  storeId,
  store = null,
  placeholder = 'Search medicine…',
  sx,
  disabled = false,
  /** @deprecated Ignored — search runs only after min typed length. */
  prefetch: _prefetch = false,
  requireStore = true,
}) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [fetchError, setFetchError] = useState(null);
  const requestIdRef = useRef(0);

  const canSearch = !requireStore || Boolean(storeId);
  const isSearching = inputValue.trim().length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(inputValue.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    if (selectedItem && getMedicineId(selectedItem) === value) return;
    const found = options.find((item) => getMedicineId(item) === value)
      || (valueItem && getMedicineId(valueItem) === value ? valueItem : null);
    if (found) setSelectedItem(found);
  }, [value, valueItem, options, selectedItem]);

  const fetchPage = useCallback(async (searchQ, nextOffset, append) => {
    if (requireStore && !storeId) return { items: [], hasMore: false };

    const requestId = ++requestIdRef.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setFetchError(null);

    try {
      const result = await searchPrescribeMedicines({
        store,
        store_id: storeId || undefined,
        location_id: storeId || undefined,
        q: searchQ,
        limit: PAGE_LIMIT,
        offset: nextOffset,
      });

      if (requestId !== requestIdRef.current) return result;

      setHasMore(result.hasMore);
      setOffset(nextOffset + result.items.length);
      setOptions((prev) => (append ? mergeMedicines(prev, result.items) : result.items));
      return result;
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setFetchError(err?.message || 'Failed to load medicines');
        if (!append) setOptions([]);
      }
      return { items: [], hasMore: false };
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [store, storeId, requireStore]);

  useEffect(() => {
    if (!open || !canSearch) return;

    const inputQuery = inputValue.trim();
    const query = debouncedSearch;
    const shouldSearch = query.length >= MIN_SEARCH_LENGTH;
    const pendingSearch = inputQuery.length >= MIN_SEARCH_LENGTH && inputQuery !== query;

    if (pendingSearch) {
      setOptions([]);
      setHasMore(false);
      return;
    }

    if (!shouldSearch) {
      setOptions([]);
      setHasMore(false);
      return;
    }

    setOffset(0);
    fetchPage(query, 0, false);
  }, [open, debouncedSearch, fetchPage, canSearch, inputValue]);

  const displayOptions = useMemo(() => {
    let list = options;
    if (!isSearching && selectedItem) {
      const id = getMedicineId(selectedItem);
      if (!options.some((item) => getMedicineId(item) === id)) {
        list = [selectedItem, ...options];
      }
    }
    if (hasMore && isSearching) return [...list, LOAD_MORE_OPTION];
    return list;
  }, [options, selectedItem, hasMore, isSearching]);

  const noOptionsText = (() => {
    if (!canSearch) return 'Select a pharmacy store to search medicines';
    if (loading) return 'Searching…';
    if (fetchError) return fetchError;
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} characters`;
    }
    return 'No medicines found';
  })();

  function handleLoadMore() {
    if (loadingMore || !hasMore) return;
    fetchPage(debouncedSearch, offset, true);
  }

  return (
    <Autocomplete
      size="small"
      disabled={disabled || !canSearch}
      open={open}
      openOnFocus
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={displayOptions}
      loading={loading}
      value={selectedItem}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInput);
        }
      }}
      onChange={(_, item) => {
        if (item?.__loadMore) {
          handleLoadMore();
          return;
        }
        setSelectedItem(item);
        onChange(getMedicineId(item), item);
      }}
      getOptionLabel={(item) => medicineOptionLabel(item)}
      isOptionEqualToValue={(a, b) => getMedicineId(a) === getMedicineId(b)}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      renderOption={(props, item) => {
        if (item?.__loadMore) {
          return (
            <Box
              component="li"
              {...props}
              key="load-more"
              sx={{ justifyContent: 'center', fontWeight: 600, color: 'primary.main' }}
            >
              {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Load more…
            </Box>
          );
        }
        const stock = item.available_quantity ?? item.stock;
        return (
          <Box component="li" {...props} key={getMedicineId(item)}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{medicineOptionLabel(item)}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {[item.generic_name, item.dosage_form].filter(Boolean).join(' · ')}
                {stock != null ? ` · Stock: ${stock}` : ''}
                {item.rate ? ` · ${formatCurrency(item.rate)}` : ''}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={canSearch ? placeholder : 'Select a store first'}
          sx={{ ...inputSx, ...sx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
